'use client';

import { ArrowUpRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { buildAdvisorMatchPathUrl } from '@ria-client/utils/advisorMatch/urls';
import { useAdUrl } from '../lib/use-ad-url';
import type { NodeProps } from '../types';
import { ContentLink } from './lib/content-link';
import { OptionPicker, type OptionPickerOption } from './lib/option-picker';
import { StepIndicator, type StepIndicatorStep } from './lib/step-indicator';

export interface MatchDynamicBannerProps extends Record<string, unknown> {
  buttonText?: string;
  buttonTextMobile?: string;
  ctaText?: string;
  ctaTextMobile?: string;
  ctaOptions?: OptionPickerOption[];
  buttonBaseUrl?: string;
  contentList?: StepIndicatorStep[];
  logo?: {
    url: string;
    alt: string;
  };
}

const DEFAULT_CONTENT_LIST: StepIndicatorStep[] = [
  { text: 'Answer a few simple questions' },
  { text: 'Get a recommended match' },
  { text: 'Start achieving your money goals' },
];

const DEFAULT_CTA_OPTIONS: OptionPickerOption[] = [
  { text: 'Financial Planning', value: 'FINANCIAL_PLANNING' },
  { text: 'Retirement Planning', value: 'RETIREMENT_PLANNING' },
  { text: 'Investment Management', value: 'INVESTMENT_MANAGEMENT' },
  { text: 'Tax Strategy', value: 'TAX_STRATEGY' },
  { text: 'Other', value: 'OTHER' },
];

const DEFAULT_BUTTON_TEXT = 'Match With An Advisor';
const DEFAULT_CTA_TEXT = "What's your financial need?";
const DEFAULT_BUTTON_BASE_URL = buildAdvisorMatchPathUrl('advisor-goal');
const DEFAULT_LOGO = {
  url: 'https://cdns3.nerdwallet.com/bain-components/nw-advisors/nw-advisors-logo-large.svg',
  alt: 'Nerdwallet advisors logo',
};

export function MatchDynamicBanner({ props }: NodeProps<MatchDynamicBannerProps>) {
  const {
    buttonText = DEFAULT_BUTTON_TEXT,
    buttonTextMobile = DEFAULT_BUTTON_TEXT,
    ctaText = DEFAULT_CTA_TEXT,
    ctaTextMobile = DEFAULT_CTA_TEXT,
    ctaOptions = DEFAULT_CTA_OPTIONS,
    buttonBaseUrl = DEFAULT_BUTTON_BASE_URL,
    contentList = DEFAULT_CONTENT_LIST,
    logo = DEFAULT_LOGO,
  } = props;
  const [selectedValue, setSelectedValue] = useState(ctaOptions[0]?.value ?? '');
  const adParams = useMemo(
    () => ({ advisorGoal: selectedValue, utm_content: 'banner' }),
    [selectedValue],
  );
  const buttonHref = useAdUrl(buttonBaseUrl, adParams);

  return (
    // #edf1f7: legacy NerdwalletAdvisorsMatchDynamicBanner container color.
    <aside
      aria-label="NerdWallet Advisors match quiz banner"
      className="w-full max-w-[740px] overflow-hidden rounded-[12px] bg-[#edf1f7] px-5 pt-6 pb-[18px]"
    >
      <div className="mb-3 flex min-w-0 items-center justify-between gap-2">
        <img
          alt={logo.alt}
          className="h-8 w-auto min-w-0 max-w-[calc(100%-5.5rem)] object-contain object-left"
          src={logo.url}
        />
        {/* 10px: legacy banner advertisement label size; no Tailwind scale match. */}
        <span className="shrink-0 rounded-[4px] bg-black/54 px-2 py-[3px] font-sans text-[10px] font-semibold text-primary-foreground sm:px-4">
          Advertisement
        </span>
      </div>

      <StepIndicator steps={contentList} />

      <div
        className="typography-body-small mb-4 font-bold text-foreground"
        id="nwam-banner-question"
      >
        <span className="hidden sm:block">{ctaText}</span>
        <span className="block sm:hidden">{ctaTextMobile}</span>
      </div>

      <OptionPicker
        ariaLabelledBy="nwam-banner-question"
        onSelect={setSelectedValue}
        options={ctaOptions}
        selected={selectedValue}
      />

      <ContentLink
        className="typography-body-small inline-flex items-center gap-1.5 rounded-[8px] bg-secondary-foreground px-5 py-2.5 font-bold text-primary-foreground no-underline transition-colors hover:bg-[var(--content-blue-dark)] focus-visible:focus-ring"
        href={buttonHref}
        rel="noopener noreferrer"
        target="_blank"
      >
        <span className="hidden sm:inline">{buttonText}</span>
        <span className="inline sm:hidden">{buttonTextMobile}</span>
        <ArrowUpRight aria-hidden="true" size={14} />
      </ContentLink>
    </aside>
  );
}
